import Firebase from "firebase/app";
import { db, FieldValue } from "../../plugins/firebase";
import { storage } from "../../plugins/firebase";

const collator = new Intl.Collator("en", {
  numeric: true,
  sensitivity: "base"
});

function getDownloadUrls(fileRefs) {
  return Promise.all(
    fileRefs.map(f => {
      return new Promise((resolve, reject) => {
        f.getDownloadURL()
          .then(url => {
            f.url = url;
            resolve(f);
          })
          .catch(reject);
      });
    })
  );
}

//Generate reasonably unique id:s
function dec2hex(dec) {
  return dec.toString(16).padStart(2, "0");
}

function generateId(len) {
  var arr = new Uint8Array((len || 40) / 2);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, dec2hex).join("");
}

export const meetings = {
  namespaced: true,
  state: {
    meetingsListener: null,
    stateListener: null,
    decisionListener: null,
    mandatesListener: null,
    meetings: [],
    state: {
      activeAgendaId: null,
      speakerId: null
    },
    decisions: [],
    manualVotes: [],
    mandates: [],
    electors: [],
    invitees: [],
    meetingFiles: []
  },
  getters: {
    getState(state) {
      return state.state;
    },
    getMeetings(state) {
      return state.meetings;
    },
    getMeeting(state) {
      return id => {
        return state.meetings.find(o => o.id === id);
      };
    },
    getDecisionPoints(state) {
      return state.decisions.sort((a, b) => collator.compare(a.text, b.text));
    },
    getManualVotes(state) {
      return state.manualVotes;
    },
    getMandates(state) {
      return state.mandates;
    },
    getElectors(state) {
      return state.electors;
    },
    getInvitees(state) {
      return state.invitees;
    },
    getMeetingFiles(state) {
      return state.meetingFiles;
    }
  },
  mutations: {
    async setMeetingFiles(state, files) {
      if (files) {
        const withUrls = await getDownloadUrls(files);

        state.meetingFiles = withUrls;
      } else {
        state.meetingFiles = [];
      }
    },
    updateActiveAgendaId(state, id) {
      state.state.activeAgendaId = id;
    },
    updateSpeakerId(state, id) {
      state.state.speakerId = id;
    },
    setState(state, data) {
      state.state = data || {
        activeAgendaId: null,
        speakerId: null
      };
    },
    async setMeetingsListener(state, listener) {
      if (!listener && state.meetingsListener) {
        // unsub
        await state.meetingsListener();
      }

      state.meetingsListener = listener;
    },
    async setStateListener(state, listener) {
      if (!listener && state.stateListener) {
        // unsub
        await state.stateListener();
      }

      state.stateListener = listener;
    },
    setMeetings(state, meetings) {
      state.meetings = meetings;
    },
    addMeeting(state, meeting) {
      if (meeting == null || meeting.deleted == true) {
        return;
      }
      const exists = state.meetings.find(o => o.id === meeting.id);
      if (exists) {
        return;
      }

      state.meetings.push(meeting);
    },
    updateMeeting(state, meeting) {
      if (meeting == null || meeting.deleted == true) {
        return;
      }
      state.meetings = state.meetings.map(o =>
        o.id === meeting.id ? meeting : o
      );
    },
    deleteMeeting(state, id) {
      state.meetings = state.meetings.filter(o => o.id !== id);
    },
    async setDecisionListener(state, listener) {
      if (!listener && state.decisionListener) {
        // unsub
        await state.decisionListener();
      }

      state.decisionListener = listener;
    },
    setDecisions(state, decisions) {
      state.decisions = decisions;
    },
    addDecision(state, decision) {
      if (decision == null || decision.deleted == true) {
        return;
      }
      const exists = state.decisions.find(o => o.id === decision.id);
      if (exists) {
        return;
      }

      state.decisions.push(decision);
    },
    updateDecision(state, decision) {
      if (decision == null || decision.deleted == true) {
        return;
      }
      state.decisions = state.decisions.map(o =>
        o.id === decision.id ? decision : o
      );
    },
    deleteDecision(state, id) {
      state.decisions = state.decisions.filter(o => o.id !== id);
    },
    async setManualVotesListener(state, listener) {
      if (!listener && state.manualVotesListener) {
        // unsub
        await state.manualVotesListener();
      }

      state.manualVotesListener = listener;
    },
    setManualVotes(state, manualVotes) {
      state.manualVotes = manualVotes;
    },
    addManualVote(state, manualVote) {
      if (manualVote == null) {
        return;
      }
      const exists = state.manualVotes.find(
        o =>
          o.id === manualVote.id &&
          o.electionId === manualVote.electionId &&
          o.meetingId === manualVote.meetingId
      );
      if (exists) {
        return;
      }
      state.manualVotes.push(manualVote);
    },
    updateManualVote(state, manualVote) {
      if (manualVote == null) {
        return;
      }
      state.manualVotes = state.manualVotes.map(o =>
        o.id === manualVote.id &&
        o.electionId === manualVote.electionId &&
        o.meetingId === manualVote.meetingId
          ? manualVote
          : o
      );
    },
    deleteManualVote(state, manualVote) {
      state.manualVotes = state.manualVotes.filter(
        o =>
          !(
            o.id === manualVote.id &&
            o.electionId === manualVote.electionId &&
            o.meetingId === manualVote.meetingId
          )
      );
    },
    async setMandatesListener(state, listener) {
      if (!listener && state.mandatesListener) {
        // unsub
        await state.mandatesListener();
      }

      state.mandatesListener = listener;
    },
    setMandates(state, mandates) {
      state.mandates = mandates;
    },
    mandatesChanged(state, changes) {
      // ADDS
      const ids = state.mandates.map(o => o.id);

      changes.adds.map(mandate => {
        if (mandate == null || mandate.deleted === true) {
          return;
        }
        const exists = ids.includes(mandate.id); //state.mandates.find(o => o.id === mandate.id);
        if (exists) {
          return;
        }
        state.mandates.push(mandate);
      });

      changes.updates.map(mandate => {
        if (mandate == null || mandate.deleted === true) {
          return;
        }
        state.mandates = state.mandates.map(o =>
          o.id === mandate.id ? mandate : o
        );
      });

      changes.deletes.map(id => {
        state.mandates = state.mandates.filter(o => o.id !== id);
      });
    },
    addMandate(state, mandate) {
      if (mandate == null || mandate.deleted === true) {
        return;
      }
      const exists = state.mandates.find(o => o.id === mandate.id);
      if (exists) {
        return;
      }
      state.mandates.push(mandate);
    },
    updateMandate(state, mandate) {
      if (mandate == null || mandate.deleted === true) {
        return;
      }
      state.mandates = state.mandates.map(o =>
        o.id === mandate.id ? mandate : o
      );
    },
    deleteMandate(state, id) {
      state.mandates = state.mandates.filter(o => o.id !== id);
    },
    async setElectorsListener(state, listener) {
      if (!listener && state.electorsListener) {
        // unsub
        await state.electorsListener();
      }

      state.electorsListener = listener;
    },
    setElectors(state, electors) {
      state.electors = electors;
    },
    electorsChanged(state, changes) {
      const ids = state.electors.map(o => o.id);

      changes.adds.map(elector => {
        if (elector == null || elector.deleted === true) {
          return;
        }
        const exists = ids.includes(elector.id);
        if (exists) {
          return;
        }
        state.electors.push(elector);
      });

      changes.updates.map(mandate => {
        if (mandate == null || mandate.deleted === true) {
          return;
        }
        state.electors = state.electors.map(o =>
          o.id === mandate.id ? mandate : o
        );
      });

      changes.deletes.map(id => {
        state.electors = state.electors.filter(o => o.id !== id);
      });
    },
    addElector(state, elector) {
      if (elector == null || elector.deleted == true) {
        return;
      }
      const exists = state.electors.find(o => o.id === elector.id);
      if (exists) {
        return;
      }

      state.electors.push(elector);
    },
    updateElector(state, elector) {
      if (elector == null || elector.deleted == true) {
        return;
      }
      state.electors = state.electors.map(o =>
        o.id === elector.id ? elector : o
      );
    },
    deleteElector(state, id) {
      state.electors = state.electors.filter(o => o.id !== id);
    },
    async setInviteesListener(state, listener) {
      if (!listener && state.inviteesListener) {
        // unsub
        await state.inviteesListener();
      }

      state.inviteesListener = listener;
    },
    setInvitees(state, invitees) {
      state.invitees = invitees;
    },
    addInvite(state, invite) {
      if (invite == null || invite.deleted == true) {
        return;
      }
      const exists = state.invitees.find(o => o.id === invite.id);
      if (exists) {
        return;
      }

      state.invitees.push(invite);
    },
    updateInvite(state, invite) {
      if (invite == null || invite.deleted == true) {
        return;
      }
      state.invitees = state.invitees.map(o =>
        o.id === invite.id ? invite : o
      );
    },
    deleteInvite(state, id) {
      state.invitees = state.invitees.filter(o => o.id !== id);
    }
  },
  actions: {
    async load({ rootState, commit }) {
      await commit("setMeetingsListener", null);
      commit("setMeetings", []);

      if (!rootState.auth.auth) {
        return;
      }

      const uid = rootState.auth.auth.uid;

      const createMeetingWithUniqueAgendaIds = doc => {
        let meeting = { id: doc.id, ...doc.data() };
        meeting.agenda = meeting.agenda.map(item => {
          if (!item.uid) {
            item.uid = generateId(10);
          }
          return item;
        });
        return meeting;
      };

      const listener = db
        .collection("meetings")
        .where("admin", "array-contains-any", [uid])
        .where("deleted", "==", false)
        .onSnapshot(snapshot => {
          snapshot.docChanges().forEach(change => {
            if (change.type === "added") {
              const meeting = createMeetingWithUniqueAgendaIds(change.doc);
              commit("addMeeting", meeting);
            }
            if (change.type === "modified") {
              const meeting = createMeetingWithUniqueAgendaIds(change.doc);
              if (meeting.deleted) {
                commit("deleteMeeting", change.doc.id);
              } else {
                commit("updateMeeting", meeting);
              }
            }
            if (change.type === "removed") {
              commit("deleteMeeting", change.doc.id);
            }
          });
        });

      await commit("setMeetingsListener", listener);
    },

    async deleteFile(context, { meeting, fileRef }) {
      const meetingId = meeting.id;
      const storageRef = storage.ref();

      try {
        await fileRef.delete();

        const res = await storageRef.child(`/files/${meetingId}/`).listAll();

        const withUrls = await getDownloadUrls(res.items);

        context.commit("setMeetingFiles", withUrls);

        const names = res.items.map(item => {
          return { name: item.name, url: item.url };
        });

        await db
          .collection("meetings")
          .doc(meetingId)
          .set({ attachments: names }, { merge: true });
      } catch (error) {
        console.log(error);
      }

      return true;
    },
    async uploadFile(context, { meeting, file }) {
      const meetingId = meeting.id;
      const storageRef = storage.ref();

      try {
        await storageRef.child(`/files/${meetingId}/` + file.name).put(file);

        const res = await storageRef.child(`/files/${meetingId}/`).listAll();

        const withUrls = await getDownloadUrls(res.items);

        context.commit("setMeetingFiles", withUrls);

        const names = res.items.map(item => {
          return { name: item.name, url: item.url };
        });

        await db
          .collection("meetings")
          .doc(meetingId)
          .set({ attachments: names }, { merge: true });
      } catch (error) {
        console.log(error);
      }

      return true;
    },
    async loadFiles(context, meeting) {
      const meetingId = meeting.id;
      const storageRef = storage.ref();

      storageRef
        .child(`/files/${meetingId}/`)
        .listAll()
        .then(res => {
          context.commit("setMeetingFiles", res.items);
        })
        .catch(error => {
          console.log("Failed to fetch files with error");
          console.log(error);
        });

      return true;
    },
    async clearFiles(context) {
      context.commit("setMeetingFiles", null);
    },

    async manualVotesListener({ commit }, { meetingId, userId }) {
      await commit("setManualVotesListener", null);
      commit("setManualVotes", []);

      const listener = db
        .collectionGroup("votes")
        .where("meetingId", "==", meetingId)
        .where("ssn", "==", userId)
        .onSnapshot(function(snapshot) {
          snapshot.docChanges().forEach(change => {
            if (change.type === "added") {
              const vote = {
                id: change.doc.id,
                electionId: change.doc.ref.parent.parent.id,
                ...change.doc.data()
              };
              commit("addManualVote", vote);
            }
            if (change.type === "modified") {
              const vote = {
                id: change.doc.id,
                electionId: change.doc.ref.parent.parent.id,
                ...change.doc.data()
              };
              commit("updateManualVote", vote);
            }
            if (change.type === "removed") {
              const vote = {
                id: change.doc.id,
                electionId: change.doc.ref.parent.parent.id,
                ...change.doc.data()
              };
              commit("deleteManualVote", vote);
            }
          });
        });

      await commit("setManualVotesListener", listener);
    },
    async stateListener({ commit }, meetingId) {
      await commit("setStateListener", null);
      commit("setState", null);

      const listener = db
        .collection("meetings")
        .doc(meetingId)
        .collection("state")
        .doc("current")
        .onSnapshot(function(doc) {
          if (doc != null && doc.data() != null) {
            const activeAgenda = doc.data().activeAgenda || null;
            commit("updateActiveAgendaId", activeAgenda);
            const speakerId = doc.data().speakerId || null;
            commit("updateSpeakerId", speakerId);
          }
        });

      await commit("setStateListener", listener);
    },
    async decisionListener({ commit }, meetingId) {
      await commit("setDecisionListener", null);
      commit("setDecisions", []);

      const listener = db
        .collection("meetings")
        .doc(meetingId)
        .collection("elections")
        .onSnapshot(snapshot => {
          snapshot.docChanges().forEach(change => {
            if (change.type === "added") {
              const decision = { id: change.doc.id, ...change.doc.data() };
              commit("addDecision", decision);
            }
            if (change.type === "modified") {
              const decision = { id: change.doc.id, ...change.doc.data() };
              commit("updateDecision", decision);
            }
            if (change.type === "removed") {
              if (change.type === "removed") {
                commit("deleteDecision", change.doc.id);
              }
            }
          });
        });

      await commit("setDecisionListener", listener);
    },
    async mandatesListener({ commit, dispatch }, meetingId) {
      await commit("setMandatesListener", null);
      commit("setMandates", []);
      const listener = db
        .collection("meetings")
        .doc(meetingId)
        .collection("mandates")
        .onSnapshot(snapshot => {
          const adds = [];
          const deletes = [];
          const updates = [];
          snapshot.docChanges().forEach(change => {
            if (change.type === "added") {
              const data = change.doc.data();
              const mandate = { id: change.doc.id, ...data };
              adds.push(mandate);
              const requestInlet =
                data.requestInlet &&
                !data.requestApproved &&
                !data.requestDenied;

              if (requestInlet) {
                dispatch("requestInlet", {
                  meetingId,
                  mandateId: mandate.id,
                  data
                });
              }
            }
            if (change.type === "modified") {
              const data = change.doc.data();
              const mandate = { id: change.doc.id, ...data };
              updates.push(mandate);
              const requestInlet =
                data.requestInlet &&
                !data.requestApproved &&
                !data.requestDenied;

              if (requestInlet) {
                dispatch("requestInlet", {
                  meetingId,
                  mandateId: mandate.id,
                  data
                });
              }
            }
            if (change.type === "removed") {
              deletes.push(change.doc.id);
            }
          });
          commit("mandatesChanged", { adds, deletes, updates });
        });

      await commit("setMandatesListener", listener);
    },
    async electorsListener({ commit }, meetingId) {
      await commit("setElectorsListener", null);
      commit("setElectors", []);

      const listener = db
        .collection("meetings")
        .doc(meetingId)
        .collection("electors")
        .onSnapshot(snapshot => {
          const adds = [];
          const deletes = [];
          const updates = [];
          snapshot.docChanges().forEach(change => {
            if (change.type === "added") {
              const data = change.doc.data();
              const elector = { id: change.doc.id, ...data };
              adds.push(elector);
            }
            if (change.type === "modified") {
              const data = change.doc.data();
              const mandate = { id: change.doc.id, ...data };
              updates.push(mandate);
            }
            if (change.type === "removed") {
              deletes.push(change.doc.id);
            }
          });
          commit("electorsChanged", { adds, deletes, updates });
        });

      await commit("setElectorsListener", listener);
    },
    async inviteesListener({ commit }, meetingId) {
      await commit("setInviteesListener", null);
      commit("setInvitees", []);

      const listener = db
        .collection("meetings")
        .doc(meetingId)
        .collection("invite-sms")
        .onSnapshot(snapshot => {
          snapshot.docChanges().forEach(change => {
            if (change.type === "added") {
              const invite = { id: change.doc.id, ...change.doc.data() };
              commit("addInvite", invite);
            }
            if (change.type === "modified") {
              const invite = { id: change.doc.id, ...change.doc.data() };
              commit("updateInvite", invite);
            }
            if (change.type === "removed") {
              if (change.type === "removed") {
                commit("deleteInvite", change.doc.id);
              }
            }
          });
        });

      await commit("setInviteesListener", listener);
    },

    createMeeting: async (
      { rootState },
      {
        name,
        organization,
        date,
        videoLink,
        contract,
        stripePaymentMethod,
        stripeCustomerId,
        promotionCode,
        askToSpeak,
        admin
      }
    ) => {
      try {
        let organizationContract =
          organization.invoicingStatus === 1
            ? organization.invoiceContractId
            : null;
        let contractOrDefault = contract ? contract : organizationContract;
        let selectedContract = rootState.contracts.contracts.find(
          c => c.id === contractOrDefault
        );
        let adminsFromContract = selectedContract?.admin
          ? selectedContract.admin
          : [];
        let adminConsolidated = [
          ...new Set([...admin, rootState.auth.auth.uid, ...adminsFromContract])
        ];
        return db.collection("meetings").add({
          name,
          organizationId: organization.id,
          organizationName: organization.name,
          voteWeightModel: organization.voteWeightModel || null,
          videoLink,
          admin: adminConsolidated,
          agenda: [],
          ballots: organization.ballots,
          date: Firebase.firestore.Timestamp.fromDate(date || new Date()),
          branding: organization.branding || null,
          deleted: false,
          contractId: contractOrDefault || null,
          stripePaymentMethod: stripePaymentMethod || null,
          stripeCustomerId: stripeCustomerId || null,
          promotionCode: promotionCode || null,
          askToSpeak: askToSpeak || false
        });
      } catch (error) {
        console.log(error);
        return;
      }
    },
    editMeeting: async (
      { rootState },
      {
        meetingId,
        name,
        date,
        videoLink,
        contract,
        stripePaymentMethod,
        stripeCustomerId,
        admin,
        promotionCode,
        askToSpeak
      }
    ) => {
      let selectedContract = rootState.contracts.contracts.find(
        c => c.id === contract
      );
      let adminsFromContract = selectedContract?.admin
        ? selectedContract.admin
        : [];
      let adminConsolidated = [
        ...new Set([...admin, rootState.auth.auth.uid, ...adminsFromContract])
      ];
      try {
        await db
          .collection("meetings")
          .doc(meetingId)
          .set(
            {
              name,
              date: date || new Date(),
              videoLink,
              contractId: contract || null,
              stripePaymentMethod: stripePaymentMethod || null,
              stripeCustomerId: stripeCustomerId || null,
              admin: adminConsolidated,
              promotionCode: promotionCode || null,
              askToSpeak: askToSpeak || false
            },
            { merge: true }
          );

        return true;
      } catch (error) {
        console.log(error);
        return;
      }
    },
    deleteMeeting: async (_, { meetingId }) => {
      try {
        await db
          .collection("meetings")
          .doc(meetingId)
          .set({ deleted: FieldValue.serverTimestamp() }, { merge: true });

        return true;
      } catch (error) {
        console.log(error);
        return false;
      }
    },
    duplicateMeeting: async (_, { meetingId }) => {
      try {
        const meetingRef = db.collection("meetings").doc(meetingId);

        const meeting = await meetingRef.get();
        const meetingData = meeting.data();
        const newMeetingRef = await db.collection("meetings").add({
          admin: meetingData.admin,
          agenda: meetingData.agenda,
          askToSpeak: meetingData.askToSpeak,
          ballots: meetingData.ballots,
          branding: meetingData.branding,
          contractId: meetingData.contractId,
          date: meetingData.date,
          deleted: false,
          name: meetingData.name + " (Kopia)",
          organizationId: meetingData.organizationId,
          organizationName: meetingData.organizationName,
          promotionCode: meetingData.promotionCode,
          stripeCustomerId: meetingData.stripeCustomerId,
          stripePaymentMethod: meetingData.stripePaymentMethod,
          videoLink: meetingData.videoLink,
          voteWeightModel: meetingData.voteWeightModel
            ? meetingData.voteWeightModel
            : "unweighted"
        });

        const elections = await meetingRef.collection("elections").get();
        const electionsArray = elections.docs;

        for (let electionIndex in electionsArray) {
          const election = electionsArray[electionIndex];
          const decisionPoint = election.data();

          let newpoint = {
            closed: null,
            opened: null,
            openVote: decisionPoint.openVote,
            text: decisionPoint.text,
            type: decisionPoint.type
          };
          if (decisionPoint.forceChoices) {
            newpoint.forceChoices = decisionPoint.forceChoices;
          }
          if (decisionPoint.alternatives) {
            newpoint.alternatives = decisionPoint.alternatives;
          }
          if (decisionPoint.choices) {
            newpoint.choices = decisionPoint.choices;
          }
          if (decisionPoint.forceUnweighted) {
            newpoint.forceUnweighted = decisionPoint.forceUnweighted;
          }

          await newMeetingRef.collection("elections").add(newpoint);
        }

        return newMeetingRef.id;
      } catch (error) {
        console.log(error);
        return false;
      }
    },
    async publishMeeting(_, meetingId) {
      await db
        .collection("meetings")
        .doc(meetingId)
        .set(
          {
            published: Firebase.firestore.Timestamp.now(),
            inlet: null,
            adminOpened: null,
            closed: null
          },
          { merge: true }
        );
    },

    async openMeetingInlet(_, meetingId) {
      await db
        .collection("meetings")
        .doc(meetingId)
        .set(
          {
            inlet: Firebase.firestore.Timestamp.now(),
            adminOpened: null,
            closed: null
          },
          { merge: true }
        );
    },

    async openMeeting(_, meetingId) {
      await db
        .collection("meetings")
        .doc(meetingId)
        .set(
          {
            adminOpened: Firebase.firestore.Timestamp.now(),
            closed: null
          },
          { merge: true }
        );
    },

    async closeMeeting(_, meetingId) {
      await db
        .collection("meetings")
        .doc(meetingId)
        .set(
          {
            closed: Firebase.firestore.Timestamp.now()
          },
          { merge: true }
        );
    },

    async clearError(_, meetingId) {
      await db
        .collection("meetings")
        .doc(meetingId)
        .set(
          {
            error: null
          },
          { merge: true }
        );
    },
    async updateSpeaker(_, { meetingId, elector }) {
      if (elector.id !== null) {
        await db
          .collection("meetings")
          .doc(meetingId)
          .collection("electors")
          .doc(elector.id)
          .set(
            {
              askToSpeak: null
            },
            { merge: true }
          );
      }
      await db
        .collection("meetings")
        .doc(meetingId)
        .collection("state")
        .doc("current")
        .set(
          {
            speaker: elector.name,
            speakerId: elector.id
          },
          { merge: true }
        );
    },
    createAgendaItem: async (_, { meetingId, id, text }) => {
      try {
        await db
          .collection("meetings")
          .doc(meetingId)
          .update({
            agenda: Firebase.firestore.FieldValue.arrayUnion({
              id: id,
              text,
              uid: generateId(10)
            })
          });
        return true;
      } catch (error) {
        console.log(error);
        return;
      }
    },
    editAgendaItem: async (_, { meetingId, newAgendaArray }) => {
      try {
        await db
          .collection("meetings")
          .doc(meetingId)
          .update({
            agenda: newAgendaArray
          });
        return true;
      } catch (error) {
        console.log(error);
        return;
      }
    },
    deleteAgendaItem: async (_, { meetingId, agendaItem }) => {
      try {
        await db
          .collection("meetings")
          .doc(meetingId)
          .update({
            agenda: Firebase.firestore.FieldValue.arrayRemove(agendaItem)
          });

        return true;
      } catch (error) {
        console.log(error);
        return;
      }
    },
    async activateAgendaItem(_, activeAgendaItem) {
      await db
        .collection("meetings")
        .doc(activeAgendaItem.meetingId)
        .collection("state")
        .doc("current")
        .set(
          {
            activeAgenda: activeAgendaItem.id
          },
          { merge: true }
        );
    },
    async uploadAgendaItems(_, { meetingId, file }) {
      var storageRef = storage.ref();

      storageRef
        .child(`/csv/meetings/${meetingId}/agenda/` + file.name)
        .put(file);

      return true;
    },
    async uploadAgendaAttachment(_, { meetingId, agendaUid, file }) {
      const storageRef = storage.ref();

      try {
        const fileName = `${agendaUid}_${file.name}`;
        await storageRef
          .child(`/agenda-attachments/${meetingId}/` + fileName)
          .put(file);

        const url = await storageRef
          .child(`/agenda-attachments/${meetingId}/` + fileName)
          .getDownloadURL();

        return { url, fileName };
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    async deleteAgendaAttachment(_, { meetingId, fileName }) {
      const storageRef = storage.ref();

      try {
        await storageRef
          .child(`/agenda-attachments/${meetingId}/` + fileName)
          .delete();

        return true;
      } catch (error) {
        console.log(error);
        return false;
      }
    },
    async createDecisionPoint(
      _,
      {
        meetingId,
        text,
        openVote,
        type,
        alternatives,
        choices,
        forceChoices,
        forceUnweighted
      }
    ) {
      try {
        const decision = {
          text,
          openVote,
          type,
          closed: null,
          opened: null
        };

        if (type === "multi" || type === "simple") {
          decision.alternatives = alternatives;
        }

        if (type === "multi") {
          decision.choices = choices;
          if (forceChoices) {
            decision.forceChoices = forceChoices;
          }
        }

        if (forceUnweighted) {
          decision.forceUnweighted = true;
        }

        await db
          .collection("meetings")
          .doc(meetingId)
          .collection("elections")
          .add(decision);
        return true;
      } catch (error) {
        console.log(error);
        return false;
      }
    },
    editDecisionPoint: async (
      _,
      {
        meetingId,
        pointId,
        text,
        openVote,
        type,
        alternatives,
        choices,
        forceChoices,
        forceUnweighted,
        weightedVoteOptional
      }
    ) => {
      try {
        const decision = {
          text,
          openVote,
          type,
          closed: null,
          opened: null
        };

        if (type === "multi" || type === "simple") {
          decision.alternatives = alternatives;
        }

        if (type === "multi") {
          decision.choices = choices;
          decision.forceChoices = forceChoices ? forceChoices : false;
        }
        if (weightedVoteOptional) {
          decision.forceUnweighted = forceUnweighted;
        }

        await db
          .collection("meetings")
          .doc(meetingId)
          .collection("elections")
          .doc(pointId)
          .set(decision, { merge: true });

        return true;
      } catch (error) {
        console.log(error);
        return;
      }
    },
    deleteDecisionPoint: async (_, { meetingId, pointId }) => {
      try {
        await db
          .collection("meetings")
          .doc(meetingId)
          .collection("elections")
          .doc(pointId)
          .delete();

        return true;
      } catch (error) {
        console.log(error);
        return false;
      }
    },
    async activateDecisionPoint(_, decisionPoint) {
      await db
        .collection("meetings")
        .doc(decisionPoint.meetingId)
        .collection("elections")
        .doc(decisionPoint.id)
        .set(
          {
            opened: Firebase.firestore.Timestamp.now()
          },
          { merge: true }
        );
    },
    async deactivateDecisionPoint(_, decisionPoint) {
      await db
        .collection("meetings")
        .doc(decisionPoint.meetingId)
        .collection("elections")
        .doc(decisionPoint.id)
        .set(
          {
            closed: Firebase.firestore.Timestamp.now()
          },
          { merge: true }
        );
    },
    async createManualVote(
      _,
      { meetingId, electionId, adminSsn, electorSsn, ballot, value }
    ) {
      try {
        await db
          .collection("meetings")
          .doc(meetingId)
          .collection("elections")
          .doc(electionId)
          .collection("votes")
          .doc(ballot)
          .set(
            {
              ssn: adminSsn,
              date: FieldValue.serverTimestamp(),
              vote: value,
              meetingId: meetingId,
              electorSsn: electorSsn
            },
            { merge: false }
          );
        return true;
      } catch (error) {
        console.log(error);
        return false;
      }
    },
    async deleteManualVote(_, { meetingId, electionId, ballot }) {
      try {
        await db
          .collection("meetings")
          .doc(meetingId)
          .collection("elections")
          .doc(electionId)
          .collection("votes")
          .doc(ballot)
          .delete();

        return true;
      } catch (error) {
        console.log(error);
        return false;
      }
    },
    async addBallot(_, { meeting, ballot }) {
      const existing = meeting.ballots[ballot.ballot];
      let ballots = {};

      if (existing && existing.ssn.includes(ballot.ssn)) return;

      ballots[ballot.ballot] = {
        ssn: existing ? [...existing.ssn, ballot.ssn] : [ballot.ssn],
        shares: ballot.shares
      };
      return db
        .collection("meetings")
        .doc(meeting.id)
        .set(
          {
            ballots: ballots
          },
          { merge: true }
        );
    },
    async deleteBallot(_, { meeting, ballot }) {
      const existing = meeting.ballots[ballot.ballot];

      if (!existing) return;

      if (!existing.ssn.includes(ballot.ssn)) return;

      if (existing.ssn.length === 1) {
        return db
          .collection("meetings")
          .doc(meeting.id)
          .update({
            [`ballots.${ballot.ballot}`]: Firebase.firestore.FieldValue.delete()
          })
          .catch(function(error) {
            console.error(error);
          });
      } else {
        let ballots = {};
        ballots[ballot.ballot] = {
          ssn: existing.ssn.filter(o => o !== ballot.ssn)
        };
        return db
          .collection("meetings")
          .doc(meeting.id)
          .set(
            {
              ballots: ballots
            },
            { merge: true }
          );
      }
    },
    async uploadBallots(_, { meetingId, file }) {
      var storageRef = storage.ref();
      if (file.name.toLowerCase().endsWith(".xlsx")) {
        storageRef
          .child(`/xlsx/meetings/${meetingId}/ballots/` + file.name)
          .put(file);
      } else if (file.name.toLowerCase().endsWith(".csv")) {
        storageRef
          .child(`/csv/meetings/${meetingId}/ballots/` + file.name)
          .put(file);
      } else {
        console.error(`Unknown file format for ${file.name}`);
      }
      return true;
    },

    async importStandardAgenda(_, { meetingId, agenda }) {
      return await db
        .collection("meetings")
        .doc(meetingId)
        .set({ agenda: agenda }, { merge: true });
    },

    async importStandardDecisions(_, { meetingId, decisions }) {
      const batch = db.batch();
      const meetingDecisionsRef = db
        .collection("meetings")
        .doc(meetingId)
        .collection("elections");
      decisions.map(d => {
        const decisionsRef = meetingDecisionsRef.doc(meetingId + "-" + d.id);
        batch.set(decisionsRef, {
          text: d.text,
          type: d.type,
          openVote: d.openVote,
          opened: null,
          closed: null
        });
      });
      return batch.commit();
    },

    async addMandate(_, { meeting, mandate }) {
      // TODO: Should we see if this alreday exists before storing it?
      return await db
        .collection("meetings")
        .doc(meeting.id)
        .collection("mandates")
        .doc(mandate.id)
        .set({
          ssn: mandate.ssn
        });
    },
    async editMandate(_, { meeting, mandate }) {
      return await db
        .collection("meetings")
        .doc(meeting.id)
        .collection("mandates")
        .doc(mandate.id)
        .set(
          {
            ssn: mandate.ssn
          },
          { merge: true }
        );
    },
    async deleteMandate(_, { meeting, mandate }) {
      return await db
        .collection("meetings")
        .doc(meeting.id)
        .collection("mandates")
        .doc(mandate.id)
        .delete();
    },
    async manuallyRegister(_, { meeting, mandate }) {
      // if (mandate.delegatedTo) {
      //   await db
      //     .collection("meetings")
      //     .doc(meeting.id)
      //     .collection("mandates")
      //     .doc(mandate.delegatedTo)
      //     .set({
      //       ssn: mandate.delegatedTo,
      //       name: mandate.name,
      //       presence: Firebase.firestore.FieldValue.serverTimestamp(),
      //       manuallyRegistered: true,
      //       adminApproved: mandate.admin
      //     });
      //   return await db
      //     .collection("meetings")
      //     .doc(meeting.id)
      //     .collection("mandates")
      //     .doc(mandate.ssn)
      //     .set({
      //       ssn: mandate.delegatedTo
      //     });
      // } else {
      return await db
        .collection("meetings")
        .doc(meeting.id)
        .collection("mandates")
        .doc(mandate.ssn)
        .set({
          ssn: mandate.ssn,
          name: mandate.name,
          presence: Firebase.firestore.FieldValue.serverTimestamp(),
          manuallyRegistered: true,
          adminApproved: mandate.admin
        });
      // }
    },
    async registerAllMembers(_, { meeting, admin }) {
      let mandates = await db
        .collection("meetings")
        .doc(meeting.id)
        .collection("mandates")
        .get();
      let existingMandates = mandates.docs.map(doc => doc.id);
      let mandatesToRegister = new Set(
        mandates.docs.map(doc => doc.data().ssn)
      );
      let batch = db.batch();
      Object.values(meeting.ballots).forEach(ballot => {
        ballot.ssn.forEach(ssn => {
          if (!existingMandates.includes(ssn)) {
            batch.set(
              db
                .collection("meetings")
                .doc(meeting.id)
                .collection("mandates")
                .doc(ssn),
              {
                ssn: ssn,
                name: ssn,
                presence: Firebase.firestore.FieldValue.serverTimestamp(),
                adminApproved: admin
              },
              { merge: true }
            );
          } else {
            console.log("Mandate already exists: " + ssn);
          }
        });
      });
      //Registrera personnumren med fullmakt, om de registreras igen p g a att de också har röstsedel spelar ingen roll
      mandatesToRegister.forEach(ssn => {
        batch.set(
          db
            .collection("meetings")
            .doc(meeting.id)
            .collection("mandates")
            .doc(ssn),
          {
            ssn: ssn,
            name: ssn,
            presence: Firebase.firestore.FieldValue.serverTimestamp(),
            adminApproved: admin
          },
          { merge: true }
        );
      });
      await batch.commit();
    },
    async requestInlet({ commit }, { meetingId, mandateId, data }) {
      // wait for name to sync
      if (!data.name) return;

      const requestInlet =
        data.requestInlet && !data.requestApproved && !data.requestDenied;

      if (requestInlet) {
        commit(
          "snackbar/push",
          {
            text: `${data.name} ansöker om att bli insläppt`,
            icon: "mdi-account",
            accept: async () => {
              await db
                .collection("meetings")
                .doc(meetingId)
                .collection("mandates")
                .doc(mandateId)
                .set(
                  {
                    backendRegisteredApproved: Firebase.firestore.FieldValue.serverTimestamp(),
                    requestApproved: new Date()
                  },
                  { merge: true }
                );
            },
            deny: async () => {
              await db
                .collection("meetings")
                .doc(meetingId)
                .collection("mandates")
                .doc(mandateId)
                .set(
                  {
                    backendRegisteredDenied: Firebase.firestore.FieldValue.serverTimestamp(),
                    requestDenied: new Date()
                  },
                  { merge: true }
                );
            }
          },
          { root: true }
        );
      }
    },
    async downloadReport(_, meetingId) {
      var storageRef = storage.ref();
      var report = storageRef.child(`reports/${meetingId}/report.html`);

      return await report.getDownloadURL();
    },
    async downloadReceipt(_, meetingId) {
      var storageRef = storage.ref();
      var receipt = storageRef.child(`receipts/${meetingId}/receipt.html`);

      return await receipt.getDownloadURL();
    },
    async uploadInvites(_, { meetingId, file }) {
      var storageRef = storage.ref();

      storageRef
        .child(`/csv/meetings/${meetingId}/invites/` + file.name)
        .put(file);

      return true;
    },
    async addInviteNumber(_, { meeting, organizationName, invite }) {
      try {
        const inviteMessage = {
          from: organizationName,
          meeting: meeting.name,
          to: invite
        };

        await db
          .collection("meetings")
          .doc(meeting.id)
          .collection("invite-sms")
          .add(inviteMessage);
      } catch (error) {
        console.log(error);
        return false;
      }

      return true;
    },
    async removeInviteNumber(_, { meeting, invite }) {
      try {
        await db
          .collection("meetings")
          .doc(meeting.id)
          .collection("invite-sms")
          .doc(invite)
          .delete();
      } catch (error) {
        console.log(error);
        return false;
      }

      return true;
    },
    async sendInviteMessages(_, { meeting, invite }) {
      return await db
        .collection("meetings")
        .doc(meeting.id)
        .collection("invite-sms")
        .doc(invite.id)
        .set(
          {
            send: Firebase.firestore.Timestamp.now()
          },
          { merge: true }
        );
    },
    async resendInviteMessages(_, { meeting, id }) {
      return await db
        .collection("meetings")
        .doc(meeting.id)
        .collection("invite-sms")
        .doc(id)
        .set(
          {
            send: null
          },
          { merge: true }
        );
    }
  }
};
